import React from 'react';
import moment from 'moment';

import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '@/lib/utils';

type EstimateAssociation = {
  estimateId?: number | null;
  estimateNumber?: string | number | null;
  qbEstimateId?: string | number | null;
  qbEstimateNumber?: string | number | null;
  internalName?: string | null;
  status?: string | null;
  statusDate?: string | null;
  legacy?: boolean | null;
};

export type EstimateStatusSummary = {
  status?: string | null;
  statusDate?: string | null;
  estimateId?: number | null;
  estimateNumber?: string | number | null;
  qbEstimateId?: string | number | null;
  qbEstimateNumber?: string | number | null;
  estimates?: EstimateAssociation[];
  additionalCount?: number;
};

type EstimateStatusCellProps = {
  summary?: EstimateStatusSummary | null;
  status?: string | null;
  date?: string | Date | null;
  estimateId?: number | null;
  estimateNumber?: string | number | null;
  qbEstimateId?: string | number | null;
  qbEstimateNumber?: string | number | null;
  canOpenEstimate?: boolean;
  onOpenEstimate?: (estimateId: number, estimate?: EstimateAssociation) => void;
  getStatusClassName?: (status: string) => string;
  align?: 'left' | 'right';
  className?: string;
  compact?: boolean;
  dataQaPrefix?: string;
  showStatusLine?: boolean;
};

const normalizeEstimateNumber = (value?: string | number | null) => {
  const raw = String(value || '').trim();
  return raw.startsWith('#') ? raw.slice(1) : raw;
};

const getQboBaseUrl = () => {
  if (typeof window === 'undefined') {
    return 'https://app.qbo.intuit.com';
  }
  const host = String(window.location.hostname || '').toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
    return 'https://sandbox.qbo.intuit.com';
  }
  const env = (localStorage.getItem('qboEnv') || '').toLowerCase();
  return env === 'sandbox' ? 'https://sandbox.qbo.intuit.com' : 'https://app.qbo.intuit.com';
};

const buildQboEstimateUrl = (estimateId?: string | number | null) => {
  const normalizedId = String(estimateId || '').trim();
  if (!normalizedId) return '';
  return `${getQboBaseUrl()}/app/estimate?txnId=${encodeURIComponent(normalizedId)}`;
};

const formatDate = (value?: string | Date | null) => {
  if (!value) return '';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('MM/DD/YY') : '';
};

const defaultStatusClassName = (status: string) => {
  const normalized = status.toLowerCase();
  if (normalized === 'exported to qb') return 'estimate_status_exported';
  if (normalized === 'sent to customer') return 'estimate_status_sent';
  if (normalized === 'created') return 'estimate_status_created text-[#2563EB]';
  return 'estimate_status_not_created';
};

const buildFallbackEstimate = ({
  estimateId,
  estimateNumber,
  qbEstimateId,
  qbEstimateNumber,
  status,
  statusDate,
}: {
  estimateId?: number | null;
  estimateNumber?: string | number | null;
  qbEstimateId?: string | number | null;
  qbEstimateNumber?: string | number | null;
  status: string;
  statusDate?: string | Date | null;
}): EstimateAssociation | null => {
  const normalizedId = Number(estimateId || 0);
  const normalizedNumber = normalizeEstimateNumber(estimateNumber || qbEstimateNumber);
  const normalizedQboId = String(qbEstimateId || '').trim();
  const normalizedQboNumber = normalizeEstimateNumber(qbEstimateNumber);
  if (!normalizedId && !normalizedNumber && !normalizedQboId && !normalizedQboNumber) return null;
  return {
    estimateId: Number.isInteger(normalizedId) && normalizedId > 0 ? normalizedId : null,
    estimateNumber: normalizedNumber,
    qbEstimateId: normalizedQboId || null,
    qbEstimateNumber: normalizedQboNumber || null,
    status,
    statusDate: statusDate ? String(statusDate) : null,
    legacy: Boolean(normalizedQboId || normalizedQboNumber),
  };
};

const EstimateBadge = ({
  estimate,
  canOpenEstimate,
  onOpenEstimate,
  dataQa,
}: {
  estimate: EstimateAssociation;
  canOpenEstimate?: boolean;
  onOpenEstimate?: (estimateId: number, estimate?: EstimateAssociation) => void;
  dataQa?: string;
}) => {
  const estimateId = Number(estimate?.estimateId || 0);
  const label = normalizeEstimateNumber(estimate?.estimateNumber || estimate?.qbEstimateNumber);
  const displayLabel = `#${label}`;
  const qbEstimateUrl = buildQboEstimateUrl(estimate?.qbEstimateId);
  if (!label) return null;

  if (!estimate?.legacy && canOpenEstimate && estimateId > 0 && onOpenEstimate) {
    return (
      <button
        type="button"
        className="block max-w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap p-0 text-left text-[12px] font-medium leading-5 text-[#2F7F98] underline underline-offset-2 hover:text-[#256D83]"
        data-qa={dataQa}
        title={displayLabel}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onOpenEstimate(estimateId, estimate);
        }}
      >
        {displayLabel}
      </button>
    );
  }

  if (qbEstimateUrl) {
    return (
      <a
        className="block max-w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap p-0 text-left text-[12px] font-medium leading-5 text-[#2F7F98] underline underline-offset-2 hover:text-[#256D83]"
        data-qa={dataQa}
        href={qbEstimateUrl}
        target="_blank"
        rel="noreferrer"
        title={displayLabel}
        onClick={(event) => event.stopPropagation()}
      >
        {displayLabel}
      </a>
    );
  }

  return (
    <span
      className="block max-w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[12px] font-medium leading-5 text-[#475569]"
      data-qa={dataQa}
      title={displayLabel}
    >
      {displayLabel}
    </span>
  );
};

const EstimateStatusCell: React.FC<EstimateStatusCellProps> = ({
  summary,
  status,
  date,
  estimateId,
  estimateNumber,
  qbEstimateId,
  qbEstimateNumber,
  canOpenEstimate,
  onOpenEstimate,
  getStatusClassName,
  align = 'left',
  className,
  compact = false,
  dataQaPrefix,
  showStatusLine = true,
}) => {
  const resolvedStatus = String(status || summary?.status || 'Not Created').trim() || 'Not Created';
  const isNotCreated = resolvedStatus.toLowerCase() === 'not created';
  const statusDate = date !== undefined && date !== '' ? date : summary?.statusDate || null;
  const displayDate = isNotCreated ? '' : formatDate(statusDate);
  const fallbackEstimate = buildFallbackEstimate({
    estimateId: summary?.estimateId || estimateId,
    estimateNumber: summary?.estimateNumber || estimateNumber,
    qbEstimateId: summary?.qbEstimateId || qbEstimateId,
    qbEstimateNumber: summary?.qbEstimateNumber || qbEstimateNumber,
    status: resolvedStatus,
    statusDate,
  });
  const estimates = (summary?.estimates?.length ? summary.estimates : fallbackEstimate ? [fallbackEstimate] : [])
    .filter((estimate) => normalizeEstimateNumber(estimate?.estimateNumber));
  const primaryEstimate = isNotCreated ? null : estimates[0] || null;
  const additionalCount = isNotCreated ? 0 : Math.max(0, estimates.length - 1);
  const statusClassName =
    getStatusClassName?.(resolvedStatus) || defaultStatusClassName(resolvedStatus);

  return (
    <div
      className={cn(
        'flex min-w-0 max-w-full flex-col gap-1',
        align === 'right' ? 'items-end text-right' : 'items-start text-left',
        compact && 'gap-0.5',
        className
      )}
      data-qa={dataQaPrefix ? `${dataQaPrefix}-estimate-status-cell` : undefined}
    >
      {showStatusLine ? (
        <div
          className={cn(
            'flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5',
            align === 'right' && 'justify-end'
          )}
        >
          <span className={cn('text-[14px] font-medium leading-5', statusClassName)}>
            {resolvedStatus}
          </span>
          {displayDate ? (
            <span className="text-[12px] leading-5 text-[#64748B]">{displayDate}</span>
          ) : null}
        </div>
      ) : null}

      {primaryEstimate ? (
        <div
          className={cn(
            'flex min-w-0 max-w-full items-center gap-1.5',
            align === 'right' && 'justify-end'
          )}
        >
          <div className="min-w-0 max-w-full">
            <EstimateBadge
              estimate={primaryEstimate}
              canOpenEstimate={canOpenEstimate}
              onOpenEstimate={onOpenEstimate}
              dataQa={dataQaPrefix ? `${dataQaPrefix}-estimate-link` : undefined}
            />
          </div>

          {additionalCount > 0 ? (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-5 shrink-0 items-center rounded-full bg-[#EAF5F8] px-2 text-[11px] font-medium leading-none text-[#2F7F98] hover:bg-[#DDEFF4]"
                  data-qa={dataQaPrefix ? `${dataQaPrefix}-estimate-more` : undefined}
                >
                  +{additionalCount}
                </button>
              </PopoverTrigger>
              <PopoverContent align={align === 'right' ? 'end' : 'start'} className="w-[220px] p-2">
                <div className="max-h-72 overflow-y-auto">
                  {estimates.map((estimate, index) => (
                    <div
                      key={`${estimate.estimateId || estimate.estimateNumber || index}-${index}`}
                      className="flex min-w-0 items-center justify-between gap-2 rounded px-2 py-1.5 hover:bg-[#F8FAFC]"
                    >
                      <div className="min-w-0">
                        <EstimateBadge
                          estimate={estimate}
                          canOpenEstimate={canOpenEstimate}
                          onOpenEstimate={onOpenEstimate}
                          dataQa={
                            dataQaPrefix
                              ? `${dataQaPrefix}-estimate-popover-link-${index}`
                              : undefined
                          }
                        />
                      </div>
                      <span className="shrink-0 text-[11px] text-[#64748B]">
                        {formatDate(estimate.statusDate)}
                      </span>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default EstimateStatusCell;
